import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ClipboardList,
  Download,
  Eye,
  Loader2,
  Mail,
  Package,
  Send,
  Undo2,
  X,
} from 'lucide-react';
import type {
  CncSpeciesAssignment,
  ProposalNodeSet,
  RfqHubFileRelease,
  RfqProject,
  SupplierInvite,
  SupplierOrg,
  SupplierShipToAddress,
  TimberSupplierStructureRef,
  VendorWorkType,
} from '../../types';
import { cncAssignmentChipLabel, cncMaterialFlowLabel } from '../../utils/cncRfqAssignments';
import { filterNodeSetsForInvite, unmatchedStructureRefs } from '../../utils/timberRfqStructureScope';
import { normalizeQuotedStructures } from '../../utils/timberQuoteStreamKey';
import {
  initialPublishStructureDraft,
  orgIdsMissingPublishStructureSelection,
} from '../../utils/rfqPublishStructureReview';
import type { HubTimberPublishPreview } from '../../utils/rfqHubTimberImport';
import {
  buildRfqPublishEmailHtml,
  defaultRfqPublishEmailMessage,
  defaultRfqReferenceReleaseEmailMessage,
  defaultRfqScopeChangeEmailMessage,
  rfqPublishEmailSubject,
  rfqProjectEmailDescriptor,
} from '../../utils/rfqPublishEmailPreview';
import { recipientFirstNameForOrg, resolveOrgsMissingRfqNotificationFirstName } from '../../utils/vendorOrgSummary';
import { resolveSupplierRfqRecipientRows } from '../../utils/supplierRfqRecipients';
import { useCRM } from '../../utils/CRMContext';
import { writeVendorPortalPreview } from '../../utils/vendorPortalPreviewStorage';
import {
  buildRfqPublishTimberPdfHtml,
  downloadRfqPublishTimberCsv,
  openRfqPublishTimberPdfPreview,
} from '../../utils/rfqPublishTimberCsvPreview';
import { useAuth } from '../../utils/AuthContext';
import {
  buildTimberSupplierCsvText,
  timberRfqEmailAttachmentFilename,
  timberSpeciesDrynessLabel,
  DEFAULT_TIMBER_RFQ_DRYNESS_SPEC,
  type TimberSpeciesDrynessEntry,
  type TimberSupplierExportOptions,
} from '../../utils/supplierTimberExport';
import { formatSupplierShipToAddress, rfqProjectSupplierShipTo, shipToContextForWorkType, type ProjectSiteAddressInput, type RfqShipToProjectRef } from '../../utils/supplierShipTo';
import { workTypeLabel } from '../../utils/vendorHelpers';
import { structureSpeciesRulesFromInvite, type StructureSpeciesRule } from '../../utils/timberRfqStructureSpecies';
import TimberCsvPreviewTable from './TimberCsvPreviewTable';
import TimberPdfPreviewPanel from './TimberPdfPreviewPanel';
import '../proposals/ProposalReviewModal.css';
import './SupplierPublishReviewModal.css';

type Tab = 'package' | 'email' | 'portal';

const COUNTDOWN_SECONDS = 7;
const ALL_TABS: Tab[] = ['package', 'email', 'portal'];

export type PublishReviewSharedFields = {
  /** 'publish' (default) or 'scope_change' — resend after editing a supplier's structures. */
  mode?: 'publish' | 'scope_change';
  /** Scope-change resends — structure names added/removed vs the published package. */
  scopeChange?: { added: string[]; removed: string[]; speciesChanges?: string[] };
  /** Timber scope-change resends — species per structure name after the change. */
  timberStructureSpecies?: Array<{ name: string; species: string[] }>;
  projectNumber?: string;
  projectName?: string;
  supplierSpeciesByOrg: Record<string, string[]>;
  supplierSpeciesDrynessByOrg: Record<string, TimberSpeciesDrynessEntry[]>;
  supplierShipToByOrg: Record<string, SupplierShipToAddress>;
  /** Structures each org is scoped to; null/absent = all structures. */
  supplierStructuresByOrg?: Record<string, TimberSupplierStructureRef[] | null>;
  /** CNC species split per org; null/absent = full package, no species split. */
  cncAssignmentsByOrg?: Record<string, CncSpeciesAssignment[] | null>;
  /** All structures the requestor may select for this publish (timber list or proposal). */
  publishStructureOptions: TimberSupplierStructureRef[];
  /** Structures the staged file was tagged to in the Project Hub — default pick when the invite has no scope. */
  stagedStructures?: TimberSupplierStructureRef[];
  /**
   * Reference release: refined file for a quote that is already approved. Suppliers
   * receive it to cut from — the review copy, email, and button never ask to requote.
   */
  referenceRelease?: boolean;
};

export type TimberPublishReviewData = PublishReviewSharedFields & {
  workType: 'timber_rfq';
  project: RfqProject;
  dealTitle: string;
  releaseVersionIndex: number;
  releaseLabel: string;
  hubFileReleaseId: string;
  proposalLinkText?: string | null;
  targets: string[];
  timberPreview: HubTimberPublishPreview;
  preload: { nodeSets: ProposalNodeSet[]; sourceFileName: string };
};

export type CncPublishReviewData = PublishReviewSharedFields & {
  workType: 'cnc_quote';
  project: RfqProject;
  dealTitle: string;
  release: RfqHubFileRelease;
  targets: string[];
};

export type SupplierPublishReviewData = TimberPublishReviewData | CncPublishReviewData;

export interface SupplierPublishReviewModalProps {
  open: boolean;
  data: SupplierPublishReviewData | null;
  supplierOrgById: Map<string, SupplierOrg>;
  supplierInvites: SupplierInvite[];
  projectSite: ProjectSiteAddressInput;
  timberRfqProject?: RfqShipToProjectRef | null;
  cncRfqProject?: RfqShipToProjectRef | null;
  publishing: boolean;
  onClose: () => void;
  onConfirm: (
    customEmailMessage: string,
    scopeStructuresByOrg: Record<string, TimberSupplierStructureRef[]>,
  ) => Promise<void>;
}

const SupplierPublishReviewModal: React.FC<SupplierPublishReviewModalProps> = ({
  open,
  data,
  supplierOrgById,
  supplierInvites,
  projectSite,
  timberRfqProject = null,
  cncRfqProject = null,
  publishing,
  onClose,
  onConfirm,
}) => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const [activeTab, setActiveTab] = useState<Tab>('package');
  const [visitedTabs, setVisitedTabs] = useState<Set<Tab>>(() => new Set<Tab>(['package']));
  const [customMessage, setCustomMessage] = useState('');
  const [previewOrgId, setPreviewOrgId] = useState('');
  const [portalFrameKey, setPortalFrameKey] = useState(0);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [pdfPreviewHtml, setPdfPreviewHtml] = useState<string | null>(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState<string | null>(null);
  const [draftStructuresByOrg, setDraftStructuresByOrg] = useState<
    Record<string, TimberSupplierStructureRef[]>
  >({});
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownMessageRef = useRef('');
  const confirmScopeRef = useRef<Record<string, TimberSupplierStructureRef[]>>({});

  const workType: VendorWorkType = data?.workType ?? 'timber_rfq';
  const dealTitle = data?.dealTitle ?? '';
  const senderName = (user?.name || user?.email || '').trim();
  const isScopeChange = data?.mode === 'scope_change';
  const isReference = !!data?.referenceRelease && !isScopeChange;

  /** "v3 — <release label>" — named in the scope-change email as the unchanged file. */
  const scopeChangeFileLabel = useMemo(() => {
    if (!data) return '';
    return data.workType === 'timber_rfq'
      ? `v${data.releaseVersionIndex} — ${data.releaseLabel}`
      : `v${data.release.releaseVersionIndex} — ${data.release.displayLabel || data.release.fileName}`;
  }, [data]);

  useEffect(() => {
    if (!open || !data) return;
    setActiveTab('package');
    setVisitedTabs(new Set<Tab>(['package']));
    const firstOrg = data.targets[0] || '';
    setPreviewOrgId(firstOrg);
    const species = firstOrg && data.workType === 'timber_rfq' ? data.supplierSpeciesByOrg[firstOrg] || [] : [];
    const firstName = recipientFirstNameForOrg(firstOrg, supplierOrgById) || 'there';
    const releaseLabelForMessage =
      data.workType === 'timber_rfq'
        ? `${data.releaseLabel} v${data.releaseVersionIndex}`
        : `${data.release.displayLabel || data.release.fileName} v${data.release.releaseVersionIndex}`;
    setCustomMessage(
      data.mode === 'scope_change'
        ? defaultRfqScopeChangeEmailMessage({
            recipientFirstName: firstName,
            workType: data.workType,
            fileLabel:
              data.workType === 'timber_rfq'
                ? `v${data.releaseVersionIndex} — ${data.releaseLabel}`
                : `v${data.release.releaseVersionIndex} — ${data.release.displayLabel || data.release.fileName}`,
          })
        : data.referenceRelease
          ? defaultRfqReferenceReleaseEmailMessage({
              recipientFirstName: firstName,
              workType: data.workType,
              releaseLabel: releaseLabelForMessage,
              structureNames: (data.stagedStructures ?? []).map(s => s.name),
            })
          : defaultRfqPublishEmailMessage({
              recipientFirstName: firstName,
              requestedSpecies: species,
              workType: data.workType,
            }),
    );
    setCountdown(null);
    const options = data.publishStructureOptions ?? [];
    setDraftStructuresByOrg(
      initialPublishStructureDraft({
        targets: data.targets,
        options,
        supplierStructuresByOrg: data.supplierStructuresByOrg,
        stagedStructures: data.stagedStructures,
      }),
    );
  }, [open, data, supplierInvites, supplierOrgById]);

  const publishStructureOptions = data?.publishStructureOptions ?? [];

  const previewOrgSpecies = previewOrgId ? data?.supplierSpeciesByOrg[previewOrgId] || [] : [];
  const previewOrgSpeciesDryness = previewOrgId ? data?.supplierSpeciesDrynessByOrg[previewOrgId] : undefined;
  /** Species narrowed per structure on the previewed supplier's invite (timber only). */
  const previewOrgStructureSpecies = useMemo((): StructureSpeciesRule[] => {
    if (!data || data.workType !== 'timber_rfq' || !previewOrgId) return [];
    const invite = data.project.timberSupplierInvites?.find(i => i.supplierOrgId === previewOrgId);
    return structureSpeciesRulesFromInvite(invite, data.publishStructureOptions ?? []);
  }, [data, previewOrgId]);
  const previewOrgShipTo = useMemo(() => {
    if (!data || !previewOrgId) return undefined;
    return rfqProjectSupplierShipTo(data.project, previewOrgId, {
      ...shipToContextForWorkType(data.workType, timberRfqProject, cncRfqProject),
      orgId: previewOrgId,
      projectSite,
    });
  }, [data, previewOrgId, projectSite, timberRfqProject, cncRfqProject]);
  const previewOrgName = previewOrgId ? supplierOrgById.get(previewOrgId)?.name || previewOrgId : '';
  const previewSpeciesSummary = previewOrgSpecies.join(', ');
  const previewDrynessSummary = previewOrgSpeciesDryness?.length
    ? timberSpeciesDrynessLabel(previewOrgSpeciesDryness)
    : '';
  const previewOrgStructures = previewOrgId
    ? draftStructuresByOrg[previewOrgId] ?? null
    : null;
  const previewOrgCncAssignments = previewOrgId
    ? data?.cncAssignmentsByOrg?.[previewOrgId] ?? undefined
    : undefined;

  const previewNodeSets = useMemo((): ProposalNodeSet[] => {
    if (!data || data.workType !== 'timber_rfq') return [];
    // The CSV is the whole timber list. Selected structures label the quote;
    // they do not remove rows.
    return data.timberPreview.nodeSets;
  }, [data]);

  /** Structures picked for the previewed supplier that are missing from this timber list. */
  const previewUnmatchedStructures = useMemo((): TimberSupplierStructureRef[] => {
    if (!data || data.workType !== 'timber_rfq' || !previewOrgStructures?.length) return [];
    return unmatchedStructureRefs(data.timberPreview.nodeSets, {
      structureScope: 'selected',
      structures: previewOrgStructures,
    });
  }, [data, previewOrgStructures]);

  /** Suppliers whose structure selection matches nothing in this list — publishing would send the wrong package. */
  const scopeMismatchOrgNames = useMemo((): string[] => {
    if (!data || data.workType !== 'timber_rfq') return [];
    const out: string[] = [];
    for (const orgId of data.targets) {
      const structures = draftStructuresByOrg[orgId];
      if (!structures?.length) continue;
      const scoped = filterNodeSetsForInvite(data.timberPreview.nodeSets, {
        structureScope: 'selected',
        structures,
      });
      if (!scoped.length) out.push(supplierOrgById.get(orgId)?.name || orgId);
    }
    return out;
  }, [data, supplierOrgById, draftStructuresByOrg]);

  const missingStructureOrgNames = useMemo((): string[] => {
    if (!data) return [];
    const missingIds = orgIdsMissingPublishStructureSelection({
      targets: data.targets,
      options: publishStructureOptions,
      draftByOrg: draftStructuresByOrg,
    });
    return missingIds.map((id) => supplierOrgById.get(id)?.name || id);
  }, [data, publishStructureOptions, draftStructuresByOrg, supplierOrgById]);

  const toggleDraftStructure = useCallback(
    (orgId: string, ref: TimberSupplierStructureRef) => {
      setDraftStructuresByOrg((prev) => {
        const current = prev[orgId] ?? [];
        const has = current.some((s) => s.id === ref.id);
        const next = has
          ? current.filter((s) => s.id !== ref.id)
          : normalizeQuotedStructures([...current, ref]);
        return { ...prev, [orgId]: next };
      });
    },
    [],
  );

  const timberExportOptionsForPreview = useMemo((): TimberSupplierExportOptions | undefined => {
    const requestedSpeciesEntries = previewOrgSpeciesDryness?.length
      ? previewOrgSpeciesDryness
      : previewOrgSpecies.length
        ? previewOrgSpecies.map((species): TimberSpeciesDrynessEntry => ({
            species,
            drynessSpec: DEFAULT_TIMBER_RFQ_DRYNESS_SPEC,
          }))
        : undefined;
    const shipToAddress = previewOrgShipTo?.line1?.trim() ? previewOrgShipTo : undefined;
    if (!requestedSpeciesEntries && !shipToAddress) return undefined;
    return {
      ...(requestedSpeciesEntries ? { requestedSpeciesEntries } : {}),
      ...(requestedSpeciesEntries && previewOrgStructureSpecies.length ? { structureSpecies: previewOrgStructureSpecies } : {}),
      ...(shipToAddress ? { shipToAddress } : {}),
    };
  }, [previewOrgSpecies, previewOrgSpeciesDryness, previewOrgShipTo, previewOrgStructureSpecies]);

  const timberPdfExportInput = useMemo(() => {
    if (!data || data.workType !== 'timber_rfq') return null;
    return {
      rfqProjectId: data.project.id,
      hubFileReleaseId: data.hubFileReleaseId || undefined,
      dealTitle: data.dealTitle,
      label: data.releaseLabel,
      sourceFileName: data.timberPreview.sourceFileName,
      nodeSets: previewNodeSets,
      requestedSpecies: previewOrgSpecies,
      speciesDryness: previewOrgSpeciesDryness,
      ...(previewOrgStructureSpecies.length ? { structureSpecies: previewOrgStructureSpecies } : {}),
      shipToAddress: previewOrgShipTo,
    };
  }, [data, previewNodeSets, previewOrgSpecies, previewOrgSpeciesDryness, previewOrgShipTo, previewOrgStructureSpecies]);

  const emailCsvFileName = useMemo(() => {
    const label = data?.workType === 'timber_rfq' ? data.releaseLabel : data?.dealTitle;
    return timberRfqEmailAttachmentFilename(label || 'timber-list');
  }, [data]);

  const emailAttachmentCsv = useMemo(() => {
    if (data?.workType !== 'timber_rfq' || previewNodeSets.length === 0) return '';
    return buildTimberSupplierCsvText({ nodeSets: previewNodeSets }, timberExportOptionsForPreview);
  }, [data?.workType, previewNodeSets, timberExportOptionsForPreview]);

  useEffect(() => {
    if (!timberPdfExportInput || activeTab !== 'package') {
      return;
    }
    let cancelled = false;
    setPdfPreviewLoading(true);
    setPdfPreviewError(null);
    void buildRfqPublishTimberPdfHtml(timberPdfExportInput)
      .then(html => {
        if (!cancelled) {
          setPdfPreviewHtml(html);
          setPdfPreviewLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setPdfPreviewHtml(null);
          setPdfPreviewError(err instanceof Error ? err.message : 'Could not build PDF preview.');
          setPdfPreviewLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [timberPdfExportInput, activeTab]);

  const packageLabel = useMemo(() => {
    if (!data) return '';
    if (data.workType === 'cnc_quote') {
      return 'CNC quote request';
    }
    return `Published from staged hub release (${data.timberPreview.sourceFileName})`;
  }, [data, previewOrgId]);

  const previewReleaseVersionIndex = useMemo(() => {
    if (!data) return undefined;
    return data.workType === 'timber_rfq'
      ? data.releaseVersionIndex
      : data.release.releaseVersionIndex;
  }, [data]);

  useEffect(() => {
    if (!data || !previewOrgId || activeTab !== 'portal') return;
    writeVendorPortalPreview({
      orgId: previewOrgId,
      orgName: previewOrgName,
      workType: data.workType,
      dealTitle: data.dealTitle,
      projectNumber: data.projectNumber,
      projectName: data.projectName,
      packageLabel,
      releaseVersionIndex: previewReleaseVersionIndex,
      requestedSpecies: previewOrgSpecies,
      timberDrynessSpec: previewOrgSpeciesDryness?.[0]?.drynessSpec,
      speciesDryness: previewOrgSpeciesDryness,
      cncSpeciesAssignments: previewOrgCncAssignments,
      cncStructures: data.workType === 'cnc_quote' ? previewOrgStructures ?? undefined : undefined,
      shipToAddress: previewOrgShipTo,
      rfqProjectId: data.project.id,
      hubFileReleaseId: data.workType === 'timber_rfq' ? data.hubFileReleaseId || undefined : undefined,
      sourceFileName: data.workType === 'timber_rfq' ? data.timberPreview.sourceFileName : undefined,
      nodeSets: data.workType === 'timber_rfq' ? previewNodeSets : undefined,
      releaseLabel:
        data.workType === 'cnc_quote'
          ? data.release.displayLabel || data.release.fileName
          : data.releaseLabel,
      linkedProposalVersionLabel:
        data.workType === 'timber_rfq' ? data.proposalLinkText || undefined : undefined,
      releaseFileName: data.workType === 'cnc_quote' ? data.release.fileName : undefined,
      releaseUrl: data.workType === 'cnc_quote' ? data.release.releasedUrl : undefined,
    });
    setPortalFrameKey((k) => k + 1);
  }, [
    activeTab,
    data,
    previewOrgId,
    previewOrgName,
    packageLabel,
    previewNodeSets,
    previewOrgSpecies,
    previewOrgSpeciesDryness,
    previewOrgCncAssignments,
    previewOrgStructures,
    previewOrgShipTo,
    previewReleaseVersionIndex,
  ]);

  const allTabsVisited = ALL_TABS.every((t) => visitedTabs.has(t));

  const handleTabClick = useCallback((tab: Tab) => {
    setActiveTab(tab);
    setVisitedTabs((prev) => {
      if (prev.has(tab)) return prev;
      const next = new Set(prev);
      next.add(tab);
      return next;
    });
  }, []);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCountdown(null);
  }, []);

  const handleStartCountdown = useCallback(() => {
    countdownMessageRef.current = customMessage;
    const scopeByOrg: Record<string, TimberSupplierStructureRef[]> = {};
    if (data) {
      for (const orgId of data.targets) {
        const picked = normalizeQuotedStructures(draftStructuresByOrg[orgId]);
        if (picked.length) scopeByOrg[orgId] = picked;
        else if (publishStructureOptions.length === 1) {
          scopeByOrg[orgId] = publishStructureOptions;
        }
      }
    }
    confirmScopeRef.current = scopeByOrg;
    setCountdown(COUNTDOWN_SECONDS);
  }, [customMessage, data, draftStructuresByOrg, publishStructureOptions]);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      clearCountdown();
      void onConfirm(countdownMessageRef.current, confirmScopeRef.current);
      return;
    }
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [countdown, clearCountdown, onConfirm]);

  useEffect(() => () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
  }, []);

  // Mirrors functions `loadSupplierEmailRecipients`: RFQ contact + portal users + pending
  // invites, narrowed to the invite's "Send to" list when staff chose one.
  const recipients = useMemo(() => {
    if (!data) return [];
    return resolveSupplierRfqRecipientRows(
      data.targets,
      supplierOrgById,
      supplierInvites,
      crmUsers,
      data.project.timberSupplierInvites,
    );
  }, [data, supplierOrgById, supplierInvites, crmUsers]);

  const orgsWithoutEmail = recipients.filter((r) => r.emails.length === 0);
  const orgsMissingFirstName = useMemo(
    () => (data ? resolveOrgsMissingRfqNotificationFirstName(data.targets, supplierOrgById) : []),
    [data, supplierOrgById],
  );

  const emailHtml = useMemo(
    () =>
      buildRfqPublishEmailHtml({
        recipientFirstName: recipientFirstNameForOrg(previewOrgId, supplierOrgById) || 'there',
        vendorOrgName: previewOrgName,
        senderName,
        dealTitle,
        projectNumber: data?.projectNumber,
        projectName: data?.projectName,
        requestedSpecies: previewOrgSpecies,
        workType,
        customMessage,
        timberDrynessSpec: previewOrgSpeciesDryness?.[0]?.drynessSpec,
        speciesDryness: previewOrgSpeciesDryness,
        cncAssignments: previewOrgCncAssignments ?? undefined,
        cncStructures: previewOrgStructures?.map(s => s.name),
        timberStructures:
          (isScopeChange || isReference) && workType === 'timber_rfq'
            ? (previewOrgStructures?.map(s => s.name).filter(Boolean).length
              ? previewOrgStructures!.map(s => s.name)
              : previewNodeSets.map(ns => ns.name))
            : undefined,
        timberStructureSpecies:
          isScopeChange && workType === 'timber_rfq' ? data?.timberStructureSpecies : undefined,
        scopeChange: isScopeChange
          ? {
              fileLabel: scopeChangeFileLabel || undefined,
              added: data?.scopeChange?.added,
              removed: data?.scopeChange?.removed,
              speciesChanges: data?.scopeChange?.speciesChanges,
            }
          : undefined,
        referenceRelease: isReference,
        releaseLabel: isReference ? scopeChangeFileLabel || undefined : undefined,
        trueDimensions: !!data && data.workType === 'cnc_quote' && data.release.trueDimensions === true,
        shipToAddress: previewOrgShipTo,
      }),
    [previewOrgId, previewOrgName, senderName, supplierOrgById, dealTitle, data?.projectNumber, data?.projectName, data && data.workType === 'cnc_quote' ? data.release.trueDimensions : undefined, data?.scopeChange, data?.timberStructureSpecies, previewOrgSpeciesDryness, previewOrgSpecies, previewOrgShipTo, previewOrgCncAssignments, previewOrgStructures, previewNodeSets, workType, customMessage, isScopeChange, isReference, scopeChangeFileLabel],
  );

  const handleDownloadEmailCsv = useCallback(async () => {
    if (!timberPdfExportInput) return;
    setCsvDownloading(true);
    try {
      await downloadRfqPublishTimberCsv(timberPdfExportInput);
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Could not download CSV.');
    } finally {
      setCsvDownloading(false);
    }
  }, [timberPdfExportInput]);

  const handleOpenPdfPreview = useCallback(() => {
    if (!pdfPreviewHtml) return;
    openRfqPublishTimberPdfPreview(pdfPreviewHtml);
  }, [pdfPreviewHtml]);

  const emailSubject = useMemo(
    () =>
      rfqPublishEmailSubject(
        data?.projectNumber,
        rfqProjectEmailDescriptor({ descriptor: data?.projectName, dealTitle: data?.dealTitle }),
        workType,
        isScopeChange,
        isReference,
      ),
    [data?.projectNumber, data?.projectName, data?.dealTitle, workType, isScopeChange, isReference],
  );

  const canPublishTimber =
    data?.workType !== 'timber_rfq' || data.timberPreview.canPublish;
  const canPublish =
    canPublishTimber &&
    !!data?.targets.length &&
    orgsMissingFirstName.length === 0 &&
    missingStructureOrgNames.length === 0;

  const structurePickerFieldset =
    publishStructureOptions.length > 0 && previewOrgId ? (
      <fieldset className="sprm-structure-picker" disabled={publishing}>
        <legend>
          {isReference
            ? `Structures this reference release covers (${previewOrgName})`
            : `Structures this quote request applies to (${previewOrgName})`}
        </legend>
        {publishStructureOptions.length >= 2 ? (
          <p className="sprm-structure-picker-hint">
            Required when the job has multiple structures. Suppliers can still upload separate PDFs
            (for example per species) and tag each upload to a structure below.
          </p>
        ) : null}
        <ul className="sprm-structure-picker-list">
          {publishStructureOptions.map((s) => {
            const checked = (draftStructuresByOrg[previewOrgId] ?? []).some((row) => row.id === s.id);
            return (
              <li key={s.id}>
                <label className="sprm-structure-picker-option">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={publishing || publishStructureOptions.length === 1}
                    onChange={() => toggleDraftStructure(previewOrgId, s)}
                  />
                  <span>{s.name || s.id}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </fieldset>
    ) : null;

  const previewOrgPicker =
    data && data.targets.length > 1 ? (
      <div className="sprm-preview-org-picker">
        <label htmlFor="sprm-preview-org">Preview as supplier</label>
        <select
          id="sprm-preview-org"
          value={previewOrgId}
          onChange={(e) => setPreviewOrgId(e.target.value)}
          disabled={publishing}
        >
          {data.targets.map((orgId) => (
            <option key={orgId} value={orgId}>
              {supplierOrgById.get(orgId)?.name || orgId}
            </option>
          ))}
        </select>
      </div>
    ) : null;

  if (!open || !data) return null;

  const releaseMeta =
    data.workType === 'timber_rfq'
      ? `v${data.releaseVersionIndex} — ${data.releaseLabel}`
      : `v${data.release.releaseVersionIndex} — ${data.release.displayLabel || data.release.fileName}`;

  return (
    <div className="prm-overlay sprm-overlay" role="presentation">
      <div className="prm-modal sprm-modal" role="dialog" aria-modal="true" aria-labelledby="sprm-title">
        <header className="prm-header">
          <div>
            <h2 id="sprm-title">
              {isScopeChange
                ? 'Review & resend — scope changed'
                : isReference
                  ? 'Review & share reference release'
                  : 'Review & publish to suppliers'}
            </h2>
            <p className="sprm-subtitle">
              {dealTitle} · {workTypeLabel(workType)} · {releaseMeta}
              {isScopeChange ? ' (same file)' : ''} ·{' '}
              {data.targets.length} supplier{data.targets.length === 1 ? '' : 's'}
            </p>
            {isReference && (
              <p className="sprm-reference-banner" role="note">
                <strong>Reference release — no requote.</strong> The awarded supplier
                {data.targets.length === 1 ? '' : 's'} will receive this refined{' '}
                {workType === 'cnc_quote' ? 'model' : 'timber list'} to cut from. Approved pricing stands and no
                quote is requested.
              </p>
            )}
            {data.workType === 'timber_rfq' && data.proposalLinkText ? (
              <p className="sprm-proposal-link">{data.proposalLinkText}</p>
            ) : null}
            {previewOrgPicker}
          </div>
          <button type="button" className="prm-close" onClick={onClose} disabled={publishing} aria-label="Close">
            <X size={20} aria-hidden />
          </button>
        </header>

        <nav className="prm-tabs" aria-label="Review sections">
          <button
            type="button"
            className={`prm-tab ${activeTab === 'package' ? 'active' : ''} ${visitedTabs.has('package') ? 'visited' : ''}`}
            onClick={() => handleTabClick('package')}
          >
            <Package size={15} aria-hidden /> Package
          </button>
          <button
            type="button"
            className={`prm-tab ${activeTab === 'email' ? 'active' : ''} ${visitedTabs.has('email') ? 'visited' : ''}`}
            onClick={() => handleTabClick('email')}
          >
            <Mail size={15} aria-hidden /> Email
          </button>
          <button
            type="button"
            className={`prm-tab ${activeTab === 'portal' ? 'active' : ''} ${visitedTabs.has('portal') ? 'visited' : ''}`}
            onClick={() => handleTabClick('portal')}
          >
            <Eye size={15} aria-hidden /> Vendor portal
          </button>
        </nav>

        <div className="prm-body sprm-body">
          {activeTab === 'package' && data.workType === 'timber_rfq' && (
            <div className="sprm-package-tab">
              {previewOrgShipTo?.line1?.trim() && (
                <p className="sprm-ship-to-preview">
                  <strong>Ship to ({previewOrgName}):</strong> {formatSupplierShipToAddress(previewOrgShipTo)}
                </p>
              )}
              {structurePickerFieldset}
              {previewUnmatchedStructures.length > 0 && previewNodeSets.length > 0 && (
                <p className="sprm-scope-warning" role="status">
                  <strong>Heads up:</strong> {previewOrgName}&apos;s selected structure
                  {previewUnmatchedStructures.length === 1 ? '' : 's'}{' '}
                  {previewUnmatchedStructures.map(s => `“${s.name}”`).join(', ')}{' '}
                  {previewUnmatchedStructures.length === 1 ? 'is' : 'are'} not named in this timber
                  list. The full list is still attached. Suppliers tag their quote upload to the structure group.
                </p>
              )}
              {scopeMismatchOrgNames.length > 0 && (
                <p className="sprm-scope-warning" role="status">
                  The structure selection for {scopeMismatchOrgNames.join(', ')} matches no group name
                  in this timber list. The full list is still attached.
                </p>
              )}
              <TimberCsvPreviewTable
                sourceFileName={data.timberPreview.sourceFileName}
                canPublish={data.timberPreview.canPublish}
                parseErrors={data.timberPreview.parseErrors}
                supplierExport={{
                  nodeSets: previewNodeSets,
                  exportOptions: timberExportOptionsForPreview,
                }}
              />
              <TimberPdfPreviewPanel
                supplierName={previewOrgName}
                speciesSummary={previewSpeciesSummary}
                drynessSummary={previewDrynessSummary}
                html={pdfPreviewHtml}
                loading={pdfPreviewLoading}
                error={pdfPreviewError}
                disabled={publishing || !canPublishTimber}
                onOpenFullPreview={handleOpenPdfPreview}
              />
            </div>
          )}

          {activeTab === 'package' && data.workType === 'cnc_quote' && (
            <div className="sprm-package-tab">
              <div className="sprm-cnc-release-card">
                <h3 className="sprm-cnc-release-title">
                  v{data.release.releaseVersionIndex} — {data.release.displayLabel || data.release.fileName}
                </h3>
                <p className="srfq-muted">
                  {data.release.fileName}
                  {data.release.releasedUrl && (
                    <>
                      {' · '}
                      <a href={data.release.releasedUrl} target="_blank" rel="noopener noreferrer">
                        Download staged file
                      </a>
                    </>
                  )}
                </p>
              </div>
              {structurePickerFieldset}
              <h4 className="sprm-section-label">Invited suppliers</h4>
              <ul className="sprm-supplier-species-list">
                {data.targets.map((orgId) => {
                  const orgName = supplierOrgById.get(orgId)?.name || orgId;
                  const assignments = data.cncAssignmentsByOrg?.[orgId] ?? null;
                  const structures = draftStructuresByOrg[orgId] ?? [];
                  return (
                    <li key={orgId}>
                      <strong>{orgName}</strong>
                      {structures.length ? (
                        <span className="sprm-cnc-structures-line">
                          Structures: {structures.map(s => s.name).join(', ')}
                        </span>
                      ) : (
                        <span className="sprm-cnc-structures-line">Structures not selected yet</span>
                      )}
                      {assignments?.length ? (
                        <div className="sprm-cnc-assignments">
                          <span className="sprm-cnc-assignment-chips">
                            {assignments.map((a) => (
                              <span key={a.species} className="sprm-cnc-assignment-chip">
                                {cncAssignmentChipLabel(a)}
                              </span>
                            ))}
                          </span>
                          <ul className="sprm-cnc-material-flow">
                            {assignments.map((a) => (
                              <li key={a.species}>
                                {cncMaterialFlowLabel(a, 'staff')}
                                {a.note ? ` — ${a.note}` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <span className="sprm-cnc-full-package"> — full package (no species split)</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {activeTab === 'email' && (
            <div className="prm-email-tab sprm-email-tab">
              <section className="sprm-email-panel">
                <h4 className="sprm-section-label">Recipients</h4>
                <ul className="sprm-recipient-list">
                  {recipients.map((row) => (
                    <li
                      key={row.orgId}
                      className={`sprm-recipient-row${
                        row.emails.length === 0 ? ' sprm-recipient-row--warn' : ''
                      }${!row.notificationFirstName ? ' sprm-recipient-row--warn' : ''}`}
                    >
                      <span className="sprm-recipient-org">{row.orgName}</span>
                      <span className="sprm-recipient-emails">
                        {row.notificationFirstName ? (
                          <>Greeting: {row.notificationFirstName}</>
                        ) : (
                          <strong>Missing RFQ notification first name</strong>
                        )}
                        {row.emails.length ? (
                          <>
                            {' '}· {row.emails.join(', ')}
                            {row.narrowed ? <em className="sprm-recipient-narrowed"> (chosen in Manage suppliers)</em> : null}
                          </>
                        ) : (
                          <> · No notification email on file</>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                {orgsMissingFirstName.length > 0 && (
                  <p className="sprm-email-warn">
                    Add a first name under RFQ notifications in Vendor Management for{' '}
                    {orgsMissingFirstName.map((o) => o.orgName).join(', ')} before publishing.
                  </p>
                )}
                {orgsWithoutEmail.length > 0 && (
                  <p className="sprm-email-warn">
                    {orgsWithoutEmail.length} supplier{orgsWithoutEmail.length === 1 ? '' : 's'} will not
                    receive an email until contact info is added in Vendor Management.
                  </p>
                )}
              </section>

              <section className="sprm-email-panel sprm-email-compose">
                <div className="prm-email-field sprm-email-subject">
                  <label>Subject</label>
                  <span>{emailSubject}</span>
                </div>

                <div className="prm-email-edit">
                  <label>Message</label>
                  <p className="sprm-email-edit-hint">
                    Shared message for all suppliers. The greeting
                    {data.workType === 'timber_rfq' ? ' and species line' : ''} update per recipient in the
                    preview below.
                  </p>
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    rows={5}
                    className="prm-email-textarea"
                    disabled={publishing}
                  />
                </div>
              </section>

              <section className="sprm-email-panel sprm-email-preview-panel">
                <div className="sprm-email-preview-header">
                  <h4 className="sprm-section-label">Email preview</h4>
                  <p className="sprm-email-preview-note">
                    As seen by <strong>{previewOrgName}</strong>
                  </p>
                </div>
                {data.workType === 'timber_rfq' && (
                  <div className="sprm-email-csv-block">
                    <div className="sprm-email-attachment-row">
                      <button
                        type="button"
                        className="sprm-email-csv-btn"
                        disabled={csvDownloading || publishing || !canPublishTimber}
                        onClick={() => void handleDownloadEmailCsv()}
                      >
                        {csvDownloading ? <Loader2 size={14} className="sprm-spin" aria-hidden /> : <Download size={14} aria-hidden />}
                        Download CSV attachment
                      </button>
                      <span className="sprm-email-csv-hint">Same files suppliers receive with the email and portal</span>
                    </div>
                    {emailAttachmentCsv ? (
                      <TimberCsvPreviewTable
                        sourceFileName={emailCsvFileName}
                        csvText={emailAttachmentCsv}
                        canPublish
                      />
                    ) : null}
                  </div>
                )}
                <div className="sprm-email-preview-frame">
                  <div className="prm-email-preview" dangerouslySetInnerHTML={{ __html: emailHtml }} />
                </div>
              </section>
            </div>
          )}

          {activeTab === 'portal' && (
            <div className="prm-client-view sprm-portal-tab">
              <iframe
                key={portalFrameKey}
                src={`/vendor-portal?publishPreview=1&k=${portalFrameKey}`}
                title="Vendor portal preview"
                className="prm-iframe"
              />
            </div>
          )}
        </div>

        <footer className="prm-footer">
          {countdown !== null ? (
            <div className="prm-countdown-bar">
              <div className="prm-countdown-left">
                <svg className="prm-countdown-ring" viewBox="0 0 36 36" width="36" height="36" aria-hidden>
                  <circle className="prm-ring-bg" cx="18" cy="18" r="15.5" fill="none" stroke="#E8E5DE" strokeWidth="3" />
                  <circle
                    className="prm-ring-progress"
                    cx="18"
                    cy="18"
                    r="15.5"
                    fill="none"
                    stroke="#2C3E2F"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 15.5}
                    strokeDashoffset={2 * Math.PI * 15.5 * (1 - countdown / COUNTDOWN_SECONDS)}
                    transform="rotate(-90 18 18)"
                  />
                </svg>
                <span className="prm-countdown-text">
                  Publishing in <strong>{countdown}s</strong>
                </span>
              </div>
              <button type="button" className="prm-btn-undo" onClick={clearCountdown} disabled={publishing}>
                <Undo2 size={15} aria-hidden /> Undo
              </button>
            </div>
          ) : publishing ? (
            <div className="prm-countdown-bar prm-sending-bar">
              <div className="prm-countdown-left">
                <Loader2 size={20} className="prm-spin" aria-hidden />
                <span className="prm-countdown-text">Sending…</span>
              </div>
            </div>
          ) : (
            <>
              <div className="prm-footer-meta">
                <span className="prm-footer-client">
                  <ClipboardList size={14} aria-hidden /> {data.targets.length} supplier
                  {data.targets.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="prm-footer-actions">
                <button type="button" className="prm-btn-cancel" onClick={onClose}>
                  Cancel
                </button>
                {!allTabsVisited && (
                  <span className="prm-send-hint">Review all tabs before publishing</span>
                )}
                {allTabsVisited && orgsMissingFirstName.length > 0 && (
                  <span className="prm-send-hint">Add RFQ notification first names in Vendor Management</span>
                )}
                {allTabsVisited && orgsMissingFirstName.length === 0 && missingStructureOrgNames.length > 0 && (
                  <span className="prm-send-hint">
                    Select structures for {missingStructureOrgNames.join(', ')} on the Package tab
                  </span>
                )}
                <button
                  type="button"
                  className="prm-btn-send"
                  disabled={!allTabsVisited || !canPublish}
                  onClick={handleStartCountdown}
                >
                  <Send size={16} aria-hidden />{' '}
                  {isScopeChange ? 'Send Scope Update' : isReference ? 'Share Reference Release' : 'Send Quote Requests'}
                </button>
              </div>
            </>
          )}
        </footer>
      </div>
    </div>
  );
};

export default SupplierPublishReviewModal;
